import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import './Locations.css';

let snapshot;
let pending;
function loadCities() {
  if (snapshot) return Promise.resolve(snapshot);
  if (!pending) pending = Promise.resolve().then(() => axios.get(`${API_BASE_URL}/localities`)).then(({ data }) => {
    if (!Array.isArray(data?.cities)) throw new Error('City list unavailable');
    snapshot = data.cities;
    return snapshot;
  }).finally(() => { pending = null; });
  return pending;
}

export default function CityPicker({ value, onChange, name = 'city', required = true, allowLocation = false, ...inputProps }) {
  const id = useId();
  const [cities, setCities] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [locating, setLocating] = useState(false);
  const [locationMessage, setLocationMessage] = useState('');
  const requestId = useRef(0);
  const changeRef = useRef(onChange);
  changeRef.current = onChange;
  useEffect(() => () => { requestId.current += 1; }, []);
  const locate = () => {
    const current = ++requestId.current;
    const finish = message => {
      if(current !== requestId.current)return;
      setLocating(false);setLocationMessage(message);
    };
    if(!navigator.geolocation){finish("המיקום אינו זמין בדפדפן הזה. בחרו יישוב ידנית.");return;}
    setLocating(true);setLocationMessage("ממתינים לאישור הגישה למיקום…");
    navigator.geolocation.getCurrentPosition(async ({coords}) => {
      if(current !== requestId.current)return;
      setLocationMessage("מחפשים יישוב קרוב…");
      try {
        const {data} = await axios.post(`${API_BASE_URL}/localities/nearest`, {
          latitude:coords.latitude, longitude:coords.longitude, accuracy:coords.accuracy,
        }, {timeout:10000});
        if(current !== requestId.current)return;
        if(!data?.city?.value)throw new Error('Missing city');
        changeRef.current({target:{name,value:data.city.value}});setQuery('');
        finish(`היישוב המוצע: ${data.city.name || data.city.value}. בדקו את היישוב לפני השמירה.`);
      }catch(error){finish(error.response?.data?.message || "לא הצלחנו לזהות את היישוב. בחרו אותו ידנית.");}
    }, error => finish(error.code === 1
      ? "הגישה למיקום לא אושרה. עדיין אפשר לבחור יישוב ידנית."
      : error.code === 3 ? "איתור המיקום נמשך זמן רב מדי. נסו שוב או בחרו יישוב ידנית."
      : "לא ניתן לזהות את המיקום. בחרו יישוב ידנית."),
    {enableHighAccuracy:false,timeout:12000,maximumAge:60000});
  };
  useEffect(() => {
    let active = true;
    setError(false);
    loadCities().then(items => { if (active) setCities(items); }).catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [attempt]);
  const selectedCity = cities.find(city => city.id === value || city.value === value);
  const matches = useMemo(() => !query.trim() ? [] : cities.filter(city => `${city.name} ${city.name_en || ''}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())).slice(0, 30), [cities, query]);
  const suggestionsVisible = open && !!query.trim() && !selectedCity && matches.length > 0;
  const choose = city => {
    requestId.current += 1; setLocating(false); setLocationMessage('');
    onChange({target:{name,value:city.value}});
    setQuery(''); setOpen(false); setActiveIndex(-1);
  };
  return <span className="city-picker">
    {allowLocation && <>
      <button className="city-location-button" type="button" disabled={locating || !cities.length} onClick={locate}>{locating ? "מאתרים את היישוב שלכם…" : "שימוש במיקום שלי"}</button>
      <small>לבחירתכם: הצעת יישוב קרוב לצורך התאמות. המיקום המדויק שלכם אינו נשמר.</small>
      {locationMessage && <small role="status" aria-live="polite">{locationMessage}</small>}
    </>}
    <input {...inputProps} role="combobox" aria-label="יישוב" aria-autocomplete="list" aria-expanded={suggestionsVisible} aria-controls={id}
      aria-activedescendant={suggestionsVisible && activeIndex >= 0 ? `${id}-${activeIndex}` : undefined}
      aria-required={required} autoComplete="off" type="text" placeholder={cities.length ? "הקלידו ובחרו יישוב" : "טוענים יישובים…"}
      value={selectedCity ? selectedCity.name : query}
      onFocus={()=>setOpen(true)}
      onBlur={()=>{setOpen(false);setActiveIndex(-1);}}
      onChange={event=>{requestId.current+=1;setLocating(false);setLocationMessage('');setQuery(event.target.value);setOpen(true);setActiveIndex(-1);if(value)onChange({target:{name,value:''}});}}
      onKeyDown={event=>{
        if(event.key==='Escape'){setOpen(false);setActiveIndex(-1);}
        if(event.key==='ArrowDown'||event.key==='ArrowUp'){event.preventDefault();setOpen(true);setActiveIndex(i=>Math.max(0,Math.min(matches.length-1,i+(event.key==='ArrowDown'?1:-1))));}
        if(event.key==='Enter'&&open){event.preventDefault();if(matches[activeIndex])choose(matches[activeIndex]);}
      }}/>
    {suggestionsVisible && <ul id={id} role="listbox" aria-label="יישובים מוצעים" className="city-suggestions">
      {matches.map((city,index)=><li id={`${id}-${index}`} role="option" aria-selected={index===activeIndex} key={city.id}
        onMouseDown={event=>event.preventDefault()} onClick={()=>choose(city)}>
        {city.name}{cities.filter(item=>item.name===city.name).length>1 ? ` (${city.id})` : ''}
      </li>)}
    </ul>}
    {query && !selectedCity && <small role="status">{matches.length ? 'בחרו יישוב מתוך ההצעות כדי להמשיך.' : 'לא נמצאו יישובים. נסו חיפוש אחר בעברית או באנגלית.'}</small>}
    {error && <small role="alert">לא ניתן לטעון את רשימת היישובים. <button type="button" onClick={() => setAttempt(count => count + 1)}>ניסיון נוסף</button></small>}
  </span>;
}
